/**
 * MAIN-879 (Fix All): pure helpers for the bulk "Fix Import Issues" panel.
 *
 * The panel reuses the review screen's own rules — getPublishProblems decides
 * what is wrong with an item, and saving goes through the same
 * reviewMenuImportProposal Edit/Reject actions as the per-item Fix button. Its
 * job is only to let a caterer settle many items in one place.
 *
 * Nothing here accepts, publishes or invents anything: prices and minimum
 * quantities are only ever what the caterer types, and a category is suggested
 * only when exactly one ACTIVE catalogue category fits.
 */
import { getEffectiveProposalFields, getPublishProblems } from "./menuImportReview.utils";

const hasValue = (value) => value !== null && value !== undefined && value !== "";
const plural = (n) => (n === 1 ? "item" : "items");

/**
 * Items Fix All can help with: still in play (not live, not rejected), not
 * held by an open order, and with at least one problem the editor can fix.
 */
export function getFixableProposals(proposals = []) {
  return proposals.filter((proposal) => {
    if (!proposal || proposal.publishedMenuId || proposal.reviewStatus === "Rejected") return false;
    // An open-order hold is not fixable at all. Other items qualify when the
    // panel can fix at least one problem; limits it cannot fix (MAIN-879
    // website currency/customization) are shown but do not hide the rest.
    return proposal.classification !== "Blocked" && fixableProblems(proposal).length > 0;
  });
}

/** The problems the panel itself can resolve. */
export function fixableProblems(proposal) {
  return getPublishProblems(proposal).filter((problem) => problem.fixable);
}

export function getFixAllCount(proposals = []) {
  return getFixableProposals(proposals).length;
}

/** Items held by an open order — shown as a note, never offered for fixing. */
export function getHeldProposals(proposals = []) {
  return proposals.filter(
    (p) => !p.publishedMenuId && p.reviewStatus !== "Rejected" && p.classification === "Blocked",
  );
}

export const isDuplicateConflict = (proposal) =>
  getPublishProblems(proposal).some((problem) => problem.field === "itemName");

// ---------------------------------------------------------------------------
// Category suggestions
// ---------------------------------------------------------------------------

export function normalizeText(value) {
  if (typeof value !== "string") return "";
  return value
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[&/,+]/g, " ")
    .replace(/[^a-z0-9 ]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

const singular = (key) =>
  key
    .split(" ")
    .map((word) =>
      word.length > 3 && word.endsWith("s") && !word.endsWith("ss") ? word.slice(0, -1) : word,
    )
    .join(" ");

/**
 * Kinds of dish and the names a catalogue category or a menu heading may use
 * for them — the same list the API uses when it files an import
 * (api/src/modules/menuImportJob/menuImportCategory.js).
 */
const CONCEPT_TITLES = new Map([
  ["appetizers", ["appetizers", "appetizer", "starters", "starter", "small plates", "apps"]],
  ["salads", ["salads", "salad", "greens"]],
  ["soups", ["soups", "soup"]],
  ["entrees", ["entrees", "entree", "mains", "main courses", "main course", "main dishes"]],
  ["sides", ["sides", "side", "side dishes", "side dish", "accompaniments", "extras"]],
  ["desserts", ["desserts", "dessert", "sweets", "sweet treats", "treats"]],
  ["beverages", ["beverages", "beverage", "drinks", "drink", "refreshments", "soft drinks"]],
  ["pizza", ["pizza", "pizzas"]],
  ["sandwiches", ["sandwiches", "sandwich", "subs", "hoagies"]],
  ["wraps", ["wraps", "wrap"]],
  ["pasta", ["pasta", "pastas"]],
  ["breakfast", ["breakfast", "brunch"]],
]);

/**
 * Dish-name keywords. Deliberately narrow: each names one kind of dish, and
 * words that cross kinds ("platter", "tray", "bowl", "chicken") are absent.
 */
const DISH_KEYWORDS = [
  ["salads", /\bsalads?\b/],
  ["soups", /\b(soups?|bisque|chowder)\b/],
  ["pizza", /\bpizzas?\b/],
  ["sandwiches", /\b(sandwich(es)?|subs?|hoagies?|paninis?|sliders?)\b/],
  ["wraps", /\bwraps?\b/],
  ["pasta", /\b(pasta|penne|spaghetti|lasagna|lasagne|ravioli|rigatoni|fettuccine|ziti)\b/],
  [
    "sides",
    /\b(potato(es)?|fries|rice|beans|coleslaw|slaw|vegetables|veggies|corn|mash|mashed|pilaf)\b/,
  ],
  [
    "desserts",
    /\b(cookies?|brownies?|cakes?|cupcakes?|tarts?|pies?|cheesecakes?|puddings?|cannoli|tiramisu|pastries)\b/,
  ],
  ["beverages", /\b(coffee|tea|lemonade|soda|sodas|juice|water|smoothies?)\b/],
  ["breakfast", /\b(pancakes?|waffles?|omelettes?|omelets?|bagels?|french toast)\b/],
];

/** Active catalogue ids answering to a kind of dish. */
function categoryIdsForConcept(concept, categories) {
  const titles = CONCEPT_TITLES.get(concept) || [];
  return (categories || [])
    .filter((category) => {
      const key = normalizeText(category.categoryTitle);
      return titles.includes(key) || titles.includes(singular(key));
    })
    .map((category) => String(category._id));
}

/** Concepts a heading part names ("Salads", "Side Dishes", "Drinks"). */
function conceptsForPart(part) {
  const key = normalizeText(part);
  if (!key) return [];
  return [...CONCEPT_TITLES.entries()]
    .filter(([, titles]) => titles.includes(key) || titles.includes(singular(key)))
    .map(([concept]) => concept);
}

/** Catalogue ids a menu heading points at, including each half of "A & B". */
export function getHeadingCandidates(categoryName, categories = []) {
  if (typeof categoryName !== "string" || !categoryName.trim()) return [];
  const parts = categoryName
    .split(/\s*(?:&|\/|,|\+|\band\b)\s*/i)
    .map((part) => part.trim())
    .filter(Boolean);
  const ids = new Set();
  parts.forEach((part) => {
    const key = normalizeText(part);
    // A real catalogue title, singular or plural, first.
    (categories || []).forEach((category) => {
      if (singular(normalizeText(category.categoryTitle)) === singular(key)) {
        ids.add(String(category._id));
      }
    });
    conceptsForPart(part).forEach((concept) =>
      categoryIdsForConcept(concept, categories).forEach((id) => ids.add(id)),
    );
  });
  return [...ids];
}

/**
 * The kind of dish a name describes. When a name hits more than one kind
 * ("Quinoa & Roasted Veg Salad"), the last word — the dish itself in English
 * menu names — decides, and only if it names exactly one kind.
 */
export function getDishConcept(itemName) {
  const name = normalizeText(itemName);
  if (!name) return null;
  const hits = DISH_KEYWORDS.filter(([, pattern]) => pattern.test(name)).map(([c]) => c);
  if (hits.length === 1) return hits[0];
  if (hits.length > 1) {
    const last = name.split(" ").pop();
    const head = DISH_KEYWORDS.filter(([, pattern]) => pattern.test(last)).map(([c]) => c);
    if (head.length === 1 && hits.includes(head[0])) return head[0];
  }
  return null;
}

/**
 * A category correction for an item with no category, or null.
 *
 * @returns {null | {
 *   categoryId: string|null,   // set only when exactly one category fits
 *   candidates: string[],      // the plausible categories when ambiguous
 *   reason: string,            // in the caterer's words
 * }}
 */
export function suggestCategory(proposal, categories = []) {
  if (!proposal) return null;
  const { itemName, categoryId } = getEffectiveProposalFields(proposal);
  if (hasValue(categoryId)) return null;

  const titleOf = (id) =>
    (categories || []).find((category) => String(category._id) === String(id))?.categoryTitle;
  const heading = proposal.categoryName;
  const headingIds = getHeadingCandidates(heading, categories);
  const concept = getDishConcept(itemName);
  const conceptIds = concept ? categoryIdsForConcept(concept, categories) : [];

  if (headingIds.length > 0) {
    const both = conceptIds.filter((id) => headingIds.includes(id));
    if (both.length === 1) {
      return {
        categoryId: both[0],
        candidates: headingIds,
        reason:
          headingIds.length > 1
            ? `"${heading}" covers more than one category; the dish name points to ${titleOf(both[0])}.`
            : `"${heading}" matches ${titleOf(both[0])}.`,
      };
    }
    if (headingIds.length === 1) {
      return {
        categoryId: headingIds[0],
        candidates: headingIds,
        reason: `"${heading}" matches ${titleOf(headingIds[0])}.`,
      };
    }
    return {
      categoryId: null,
      candidates: headingIds,
      reason: `"${heading}" could be ${headingIds.map(titleOf).join(" or ")} — choose one.`,
    };
  }

  if (conceptIds.length === 1) {
    return {
      categoryId: conceptIds[0],
      candidates: conceptIds,
      reason: `Suggested from the dish name: ${titleOf(conceptIds[0])}.`,
    };
  }
  return null;
}

// ---------------------------------------------------------------------------
// Drafts: what the caterer has typed for an item in the panel
// ---------------------------------------------------------------------------

/**
 * The starting draft for an item: its current values, plus a category only
 * when one can be suggested reliably. Prices and minimum quantities are never
 * filled in.
 */
export function buildInitialDraft(proposal, categories = []) {
  const fields = getEffectiveProposalFields(proposal);
  const suggestion = suggestCategory(proposal, categories);
  return {
    itemName: fields.itemName || "",
    categoryId: fields.categoryId || suggestion?.categoryId || "",
    price: hasValue(fields.price) ? String(fields.price) : "",
    minQuantity: hasValue(fields.minQuantity) ? String(fields.minQuantity) : "",
    choice: "fix",
    suggestedCategory: Boolean(!fields.categoryId && suggestion?.categoryId),
  };
}

/**
 * Field-level problems with what the caterer typed — a value that would be
 * rejected, not a value still missing. A blank field is allowed: saving the
 * category for twenty items now and their prices later is fine.
 */
export function validateDraft(draft) {
  const errors = {};
  if (!draft || draft.choice === "skip") return errors;
  if (hasValue(draft.price) && !(Number(draft.price) > 0)) {
    errors.price = "Enter a price greater than 0";
  }
  if (
    hasValue(draft.minQuantity) &&
    !(Number.isInteger(Number(draft.minQuantity)) && Number(draft.minQuantity) > 0)
  ) {
    errors.minQuantity = "Enter a whole number greater than 0";
  }
  // A blank name is not an error: it is never saved, so the item keeps its own.
  return errors;
}

/**
 * The editedFields to save — only values the caterer changed from what the
 * item already has, so existing valid values are never rewritten. The server
 * merges these over the item's earlier edits.
 */
export function buildDraftEdits(proposal, draft) {
  if (!proposal || !draft || draft.choice === "skip") return {};
  const fields = getEffectiveProposalFields(proposal);
  const edits = {};
  const name = String(draft.itemName ?? "").trim();
  if (name && name !== String(fields.itemName ?? "").trim()) edits.itemName = name;
  if (draft.categoryId && String(draft.categoryId) !== String(fields.categoryId ?? "")) {
    edits.categoryId = draft.categoryId;
  }
  // Number(null) is 0, so a missing value is settled before comparing: a
  // typed "0" against no price is a change (and then fails validation).
  const numberChanged = (typed, current) =>
    hasValue(typed) && (!hasValue(current) || Number(typed) !== Number(current));
  if (numberChanged(draft.price, fields.price)) edits.price = Number(draft.price);
  if (numberChanged(draft.minQuantity, fields.minQuantity)) {
    edits.minQuantity = Number(draft.minQuantity);
  }
  return edits;
}

/** The item as it would read after saving this draft. */
export function applyDraft(proposal, draft) {
  return {
    ...proposal,
    editedFields: { ...(proposal?.editedFields || {}), ...buildDraftEdits(proposal, draft) },
  };
}

/** Problems that would remain after saving this draft. */
export function getDraftProblems(proposal, draft) {
  if (draft?.choice === "skip") return [];
  return fixableProblems(applyDraft(proposal, draft));
}

/** Whether saving this draft would change anything. */
export function isDraftDirty(proposal, draft) {
  if (draft?.choice === "skip") return true;
  return Object.keys(buildDraftEdits(proposal, draft)).length > 0;
}

/**
 * What to send for one item: a Reject when the caterer chose to skip a
 * duplicate, an Edit with only the changed fields, or nothing.
 */
export function buildSaveRequest(proposal, draft) {
  if (!proposal || !draft) return null;
  if (draft.choice === "skip") return { proposalId: proposal._id, action: "Reject" };
  const editedFields = buildDraftEdits(proposal, draft);
  if (Object.keys(editedFields).length === 0) return null;
  return { proposalId: proposal._id, action: "Edit", editedFields };
}

/**
 * How the panel shows one item, from the saved state first: an item whose
 * saved values no longer have a problem is fixed whatever its draft says.
 */
export function getRowStatus(proposal, draft, saveError) {
  if (!proposal) return { key: "missing", label: "No longer in this import" };
  if (proposal.reviewStatus === "Rejected") return { key: "skipped", label: "Skipped" };
  if (proposal.publishedMenuId) return { key: "fixed", label: "Published" };
  if (fixableProblems(proposal).length === 0) return { key: "fixed", label: "Fixed" };
  if (saveError) return { key: "failed", label: `Couldn't save: ${saveError}` };
  if (Object.keys(validateDraft(draft)).length)
    return { key: "invalid", label: "Check the values" };
  if (draft?.choice === "skip") return { key: "ready", label: "Will be skipped" };
  if (isDraftDirty(proposal, draft) && getDraftProblems(proposal, draft).length === 0) {
    return { key: "ready", label: "Ready to save" };
  }
  if (isDraftDirty(proposal, draft)) return { key: "partial", label: "Partly fixed" };
  return { key: "open", label: "Needs input" };
}

/**
 * Items holding changes the caterer made in the panel that are not saved yet —
 * what leaving the panel would throw away. Only items the caterer touched
 * count: a category the panel prepared on its own is not their work, so a
 * panel opened and closed untouched never asks. A change whose save failed is
 * still unsaved; an item that is now fixed or skipped is not.
 */
export function getUnsavedIds({ touched = [], drafts = {}, proposals = [], saveErrors = {} } = {}) {
  const byId = new Map(proposals.map((p) => [String(p._id), p]));
  return [...touched].filter((id) => {
    const proposal = byId.get(String(id));
    if (!proposal) return false;
    const status = getRowStatus(proposal, drafts[id], saveErrors[id]).key;
    if (status === "fixed" || status === "skipped" || status === "missing") return false;
    return isDraftDirty(proposal, drafts[id]);
  });
}

/**
 * Progress over the items the panel opened with: "5 of 12 issues resolved".
 * Resolved means the saved item no longer has a problem, or was skipped.
 */
export function getFixProgress(openedIds = [], proposals = []) {
  const byId = new Map(proposals.map((p) => [String(p._id), p]));
  let resolved = 0;
  openedIds.forEach((id) => {
    const proposal = byId.get(String(id));
    if (
      !proposal ||
      proposal.reviewStatus === "Rejected" ||
      proposal.publishedMenuId ||
      fixableProblems(proposal).length === 0
    ) {
      resolved += 1;
    }
  });
  return { resolved, total: openedIds.length, remaining: openedIds.length - resolved };
}

/**
 * The truthful outcome of a bulk save, worked out from the refreshed items.
 * Never says everything was fixed while anything the panel opened with still
 * has a problem.
 */
export function buildBulkFixMessage({ saved = 0, failed = 0, remaining = 0 } = {}) {
  const issueWord = remaining === 1 ? "issue" : "issues";
  if (saved === 0 && failed === 0) {
    return { variant: "info", message: "Nothing to save — change a value or choose a fix first." };
  }
  if (failed > 0) {
    return {
      variant: "error",
      message: `${saved} ${plural(saved)} saved. ${failed} ${plural(failed)} couldn't be saved — they're marked in the list.${
        remaining ? ` ${remaining} ${issueWord} still open.` : ""
      }`,
    };
  }
  if (remaining > 0) {
    return {
      variant: "warning",
      message: `${saved} ${plural(saved)} saved. ${remaining} ${issueWord} still need${remaining === 1 ? "s" : ""} attention.`,
    };
  }
  return { variant: "success", message: `${saved} ${plural(saved)} saved. All issues resolved.` };
}
